import React from "react";
import { Stack, Group, Text, Badge } from "@mantine/core";

const BONUS_CATEGORY_LABELS = {
  ITEM: "Item",
  MONEY: "Money",
  RECURRING: "Recurring",
};

const ITEM_EARNED_BY_LABELS = {
  REVENUE: "Revenue",
  ACCOUNTS: "Accounts",
  RECRUITS: "Recruits",
};

const EARNED_BY_TYPE_LABELS = {
  SERVICED: "Serviced",
  QUALIFIED: "Qualified",
  GET_TO_MARKET: "Get to Market",
  FINISH_SUMMER: "Finish Summer",
};

const SCHEDULE_TYPE_LABELS = {
  WEEKLY: "Weekly",
  BIWEEKLY: "Biweekly",
  MONTHLY: "Monthly",
};

const DAY_OF_WEEK_LABELS = {
  1: "Sunday",
  2: "Monday",
  3: "Tuesday",
  4: "Wednesday",
  5: "Thursday",
  6: "Friday",
  7: "Saturday",
};

const formatCurrency = value =>
  value == null
    ? "N/A"
    : `$${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatDate = dateString => {
  if (!dateString) return "N/A";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  const d = match
    ? new Date(
        parseInt(match[1], 10),
        parseInt(match[2], 10) - 1,
        parseInt(match[3], 10)
      )
    : new Date(dateString);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Detail = ({ label, value }) => (
  <Group gap="xs">
    <Text size="sm" c="dimmed">
      {label}:
    </Text>
    <Text size="sm">{value}</Text>
  </Group>
);

// document_text comes back as a related object ({ document_text, ... }) but may
// also be a plain string; normalize to the underlying text.
const getDocumentText = value =>
  value && typeof value === "object" ? value.document_text : value;

// Read-only display of a bonus item, matching the main page layout.
function BonusDisplay({ item }) {
  if (!item) return null;

  const documentText = getDocumentText(item.document_text);

  return (
    <Stack gap="xs">
      <Group gap="xs" align="center">
        <Text size="sm" fw={600}>
          {item.name}
        </Text>
        {item.bonus_type_category && (
          <Badge variant="light">
            {BONUS_CATEGORY_LABELS[item.bonus_type_category] ??
              item.bonus_type_category}
          </Badge>
        )}
      </Group>
      {documentText && (
        <Text size="sm" c="#444" style={{ whiteSpace: "pre-wrap" }}>
          {documentText}
        </Text>
      )}
      <Group gap="xl">
        {item.bonus_type_earned_by_item != null && (
          <Detail
            label="Earned By"
            value={
              ITEM_EARNED_BY_LABELS[item.bonus_type_earned_by_item] ??
              item.bonus_type_earned_by_item
            }
          />
        )}
        {item.bonus_type_earned_by_type != null && (
          <Detail
            label="Earned By Type"
            value={
              EARNED_BY_TYPE_LABELS[item.bonus_type_earned_by_type] ??
              item.bonus_type_earned_by_type
            }
          />
        )}
        <Detail
          label="Bonus Amount"
          value={formatCurrency(item.bonus_amount)}
        />

        {item.bonus_type_category === "ITEM" && (
          <>
            {item.item_name != null && (
              <Detail label="Item" value={item.item_name} />
            )}
            {item.item_monetary_value != null && (
              <Detail
                label="Item Value"
                value={formatCurrency(item.item_monetary_value)}
              />
            )}
          </>
        )}

        {item.bonus_type_category === "RECURRING" && (
          <>
            {item.recurring_initial_amount != null && (
              <Detail
                label="Initial Amount"
                value={formatCurrency(item.recurring_initial_amount)}
              />
            )}
            {item.recurring_repeated_amount != null && (
              <Detail
                label="Repeated Amount"
                value={formatCurrency(item.recurring_repeated_amount)}
              />
            )}
            {item.recurring_schedule_type != null && (
              <Detail
                label="Schedule"
                value={
                  SCHEDULE_TYPE_LABELS[item.recurring_schedule_type] ??
                  item.recurring_schedule_type
                }
              />
            )}
            {item.recurring_start_date != null && (
              <Detail
                label="Start"
                value={formatDate(item.recurring_start_date)}
              />
            )}
            {item.recurring_end_date != null && (
              <Detail label="End" value={formatDate(item.recurring_end_date)} />
            )}
            {item.recurring_day_of_week != null && (
              <Detail
                label="Day of Week"
                value={
                  DAY_OF_WEEK_LABELS[String(item.recurring_day_of_week)] ??
                  item.recurring_day_of_week
                }
              />
            )}
            {item.recurring_day_of_month != null && (
              <Detail
                label="Day of Month"
                value={item.recurring_day_of_month}
              />
            )}
          </>
        )}
      </Group>
    </Stack>
  );
}

export default BonusDisplay;
